import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useChildren } from "@/context/ChildrenContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { BookHeart, Loader2, Sparkles, Trash2 } from "lucide-react";
import CoachMarkdown from "@/components/CoachMarkdown";
import Paywall from "@/components/Paywall";
import StoryAudioPlayer from "@/components/StoryAudioPlayer";
import { Link } from "react-router-dom";
import Mascot from "@/components/Mascot";

const THEMES = [
  { key: "aventure", label: "🌟 Aventure douce" },
  { key: "amitie", label: "🐻 Amitié" },
  { key: "courage", label: "🦁 Courage" },
  { key: "famille", label: "🏡 Famille" },
  { key: "nature", label: "🌳 Nature magique" },
  { key: "reves", label: "🌙 Rêves étoilés" },
  { key: "kindness", label: "💛 Gentillesse" },
  { key: "curiosite", label: "🔎 Curiosité" },
];

const LENGTHS = [
  { key: "short", label: "Court (2 min)" },
  { key: "medium", label: "Moyen (4 min)" },
  { key: "long", label: "Long (7 min)" },
];

export default function StoriesPage() {
  const { user } = useAuth();
  const { activeChild } = useChildren();
  const [stories, setStories] = useState([]);
  const [theme, setTheme] = useState("aventure");
  const [customTheme, setCustomTheme] = useState("");
  const [length, setLength] = useState("short");
  const [busy, setBusy] = useState(false);
  const [reading, setReading] = useState(null);

  const load = async () => {
    const { data } = await api.get("/stories");
    setStories(data);
  };
  useEffect(() => { load(); }, []);

  const generate = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const finalTheme = customTheme.trim() || THEMES.find((t) => t.key === theme)?.label.replace(/^[^\s]+\s/, "") || "aventure";
      const { data } = await api.post("/stories/generate", {
        child_id: activeChild?.id,
        theme: finalTheme,
        length,
      });
      toast.success("Conte créé 📖");
      setReading(data);
      setCustomTheme("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur de génération");
    }
    setBusy(false);
  };

  const remove = async (id) => {
    await api.delete(`/stories/${id}`);
    toast.success("Conte supprimé");
    if (reading?.id === id) setReading(null);
    load();
  };

  const access = user?.access;
  const locked = access && access.status === "expired";

  return (
    <div className="grid lg:grid-cols-2 gap-8" data-testid="stories-page">
      {locked ? (
        <div className="lg:col-span-2">
          <Paywall
            title="Les contes du soir sont réservés aux abonnés"
            message="Débloque Kynoria à vie pour 47 € et crée des contes personnalisés à volonté."
          />
        </div>
      ) : (
      <>
      <section className="space-y-5">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-2xl bg-[#E3EAF7] grid place-items-center">
            <BookHeart className="h-6 w-6 text-[#23375E]" />
          </div>
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-[#16233D]" style={{ fontFamily: "Outfit, sans-serif" }}>
              Contes du soir
            </h1>
            <p className="text-[#77849C] text-sm">
              Générés avec le prénom {activeChild ? `de ${activeChild.name}` : "de ton enfant"}
            </p>
          </div>
          <Mascot pose="sleep" size={110} anim="float" className="hidden sm:block shrink-0 mascot-pop" />
        </div>

        <div className="soft-card p-6 space-y-4">
          {!activeChild && (
            <p className="text-sm text-[#E8899F]">Ajoute d'abord un enfant pour personnaliser le conte.</p>
          )}
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-[#77849C] mb-2">Thème</p>
            <div className="flex flex-wrap gap-2">
              {THEMES.map((t) => (
                <button
                  key={t.key}
                  data-testid={`theme-${t.key}`}
                  onClick={() => setTheme(t.key)}
                  className={`rounded-full px-3 py-1.5 text-sm border transition ${
                    theme === t.key ? "bg-[#23375E] text-white border-[#23375E]" : "bg-white border-[#E3E8F1] text-[#44506B] hover:border-[#23375E]"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-[#77849C] mb-2">Thème personnalisé (optionnel)</p>
            <Textarea
              data-testid="custom-theme-input"
              value={customTheme}
              onChange={(e) => setCustomTheme(e.target.value)}
              placeholder="Ex : un petit renard qui apprend à partager ses jouets"
              rows={2}
            />
          </div>

          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-[#77849C] mb-2">Longueur</p>
            <div className="flex gap-2">
              {LENGTHS.map((l) => (
                <button
                  key={l.key}
                  data-testid={`length-${l.key}`}
                  onClick={() => setLength(l.key)}
                  className={`rounded-full px-4 py-1.5 text-sm border transition ${
                    length === l.key ? "bg-[#23375E] text-white border-[#23375E]" : "bg-white border-[#E3E8F1] text-[#44506B] hover:border-[#23375E]"
                  }`}
                >
                  {l.label}
                </button>
              ))}
            </div>
          </div>

          <Button
            data-testid="generate-story-btn"
            onClick={generate}
            disabled={busy || !activeChild}
            className="w-full brand-btn rounded-full py-6 text-base font-semibold"
          >
            {busy ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Le conteur écrit...</>
            ) : (
              <><Sparkles className="h-4 w-4 mr-2" /> Générer un conte</>
            )}
          </Button>
          {access && access.status !== "expired" && (
            <p className="text-xs text-center text-[#77849C]">
              Formule : {access.tier === "famille" ? "Famille+ (illimité)" : `${access.stories_per_month} contes / mois`}
              {" · "}
              <Link to="/abonnement" className="text-[#23375E] underline">Changer</Link>
            </p>
          )}
        </div>
      </section>

      <section className="space-y-4">
        <h2 className="text-2xl font-bold text-[#16233D]" style={{ fontFamily: "Outfit, sans-serif" }}>
          Bibliothèque
        </h2>
        {reading && (
          <article className="soft-card p-6 !bg-[#FFF4DC]" data-testid="story-reader">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-bold uppercase tracking-wider text-[#16233D]">
                {reading.theme} · pour {reading.child_name}
              </p>
              <button onClick={() => setReading(null)} className="text-xs text-[#16233D] underline">
                Fermer
              </button>
            </div>
            <CoachMarkdown text={reading.content} />
            <StoryAudioPlayer storyId={reading.id} />
          </article>
        )}
        {stories.length === 0 && !reading && (
          <div className="soft-card p-8 text-center" data-testid="stories-empty-state">
            <div className="flex justify-center">
              <Mascot pose="sleep" size={140} anim="float" className="mascot-pop" />
            </div>
            <p className="mt-4 text-[#77849C]">Aucun conte pour le moment. Crée le premier !</p>
          </div>
        )}
        <div className="space-y-3">
          {stories.map((s) => (
            <article
              key={s.id}
              data-testid={`story-${s.id}`}
              className="soft-card p-4 flex items-center gap-3"
            >
              <div className="h-10 w-10 rounded-xl bg-[#E3EAF7] grid place-items-center text-lg">📖</div>
              <button onClick={() => setReading(s)} className="flex-1 text-left">
                <p className="font-semibold text-[#16233D] truncate">
                  {s.theme} · {s.child_name}
                </p>
                <p className="text-xs text-[#77849C]">
                  {new Date(s.created_at).toLocaleDateString("fr-FR", { dateStyle: "medium" })}
                </p>
              </button>
              <button
                data-testid={`del-story-${s.id}`}
                onClick={() => remove(s.id)}
                className="text-[#77849C] hover:text-red-500"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </article>
          ))}
        </div>
      </section>
      </>
      )}
    </div>
  );
}
